import React, { useContext, useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { InputMoney, InputSelect, InputText } from '~/components/NovosInputs';
import { usePedidoVenda } from '~/pages/PedidoVenda/PedidoVendaContext';
import { formatToCurrency } from '~/pages/AlteracaoDePrecoPorItem/utils/formatToCurrency';
import { formatCurrencyAsText, transformAsCurrency } from '~/utils/functions';
import { convertToDecimal } from '~/pages/AlteracaoDePrecoPorItem/utils/convertToDecimal';
import api from '~/services/api';
import { LojaContext } from '~/context/loja';
import Separator from '~/components/Separator';

export type SelectType = {
  value: number | string;
  label: string;
};

export const FormFinanceiro: React.FC = () => {
  const { codLoja } = useContext(LojaContext);
  const {
    parcelasRows,
    formPedidoVenda,
    valorTotal,
    subTotalFinanceiro,
    isDisabledPedido,
    setSubTotalFinanceiro,
    isUpdate,
    perfil,
    formFinanceiro,
  } = usePedidoVenda();
  const [totalFinanceiro, setTotalFinanceiro] = useState(0);
  const [valAcrescimo, setValAcrescimo] = useState(0);
  const [valDesconto, setValDesconto] = useState(0);
  const [categorias, setCategorias] = useState<SelectType[]>([]);
  const [contas, setContas] = useState<SelectType[]>([]);

  const { control, register, setValue, getValues, clearErrors } =
    formPedidoVenda;

  const {
    setValue: setValueFinanceiro,
    control: controlFinanceiro,
    register: registerFinanceiro,
    clearErrors: clearErrorsFinanceiro,
    formState: { errors },
  } = formFinanceiro;

  useEffect(() => {
    let totalParcelas = 0;
    parcelasRows.map((parcela: any) => {
      totalParcelas += parcela.val_parcela || 0;
      return parcela;
    });
    setTotalFinanceiro(totalParcelas);
  }, [parcelasRows, setTotalFinanceiro]);

  useEffect(() => {
    const { val_desconto, val_acrescimo } = getValues();
    let updateSubFinanceiro = 0;
    updateSubFinanceiro =
      transformAsCurrency(valorTotal) +
      transformAsCurrency(val_acrescimo) -
      transformAsCurrency(val_desconto);
    if (
      isUpdate &&
      updateSubFinanceiro !== transformAsCurrency(subTotalFinanceiro)
    ) {
      setSubTotalFinanceiro(transformAsCurrency(updateSubFinanceiro));
    }
  }, [valorTotal, subTotalFinanceiro, isUpdate]);

  // Categorias
  useEffect(() => {
    (async () => {
      if (perfil && perfil?.label.toLowerCase() !== 'selecione') {
        const tipo_categoria = perfil.tipo_operacao === 1 ? 0 : 1;
        const { data } = await api.get(
          `/perfil-loja/categorias/${tipo_categoria}`,
        );
        const option = data.data.map((categoria: any) => {
          return {
            value: categoria.cod_categoria,
            label: categoria.descricao,
            ...categoria,
          };
        });

        setCategorias(option);
      }
    })();
  }, [perfil]);

  // Contas
  useEffect(() => {
    (async () => {
      if (codLoja) {
        const { data } = await api.get(`/contas/loja/${codLoja}`);
        const option = data.data.map((conta: any) => {
          return {
            value: conta.cod_cc,
            label: conta.descricao,
            ...conta,
          };
        });

        setContas(option);
      }
    })();
  }, [codLoja]);

  const subValor = transformAsCurrency(valorTotal);
  const handleCalcDesconto = (campo: string, value: number) => {
    let valueDesconto = 0;
    let valueAcrescimo = 0;
    let auxSubTotal = 0;

    if (campo === 'val_desconto') {
      valueDesconto += value;
      setValDesconto(valueDesconto);
      auxSubTotal = subValor - valueDesconto + valAcrescimo;
    }
    if (campo === 'val_acrescimo') {
      valueAcrescimo += value;
      setValAcrescimo(valueAcrescimo);
      auxSubTotal = subValor + valueAcrescimo - valDesconto;
    }
    setSubTotalFinanceiro(auxSubTotal);
    return auxSubTotal;
  };

  return (
    <>
      <Row className="d-flex align-items-end">
        <Col sm={12} md={4}>
          <InputSelect
            ListaWidth="580px"
            label="Categoria"
            placeholder="Selecione..."
            name="categoria"
            register={registerFinanceiro}
            disabled={isDisabledPedido}
            isError={!!errors.categoria}
            control={controlFinanceiro}
            options={categorias}
            changeSelected={(selected) => {
              clearErrorsFinanceiro('categoria');
              console.log(selected);
              setValueFinanceiro('categoria', selected);
            }}
          />
        </Col>
        <Col sm={12} md={4}>
          <InputSelect
            ListaWidth="480px"
            label="Conta"
            placeholder="Selecione..."
            name="conta"
            register={registerFinanceiro}
            disabled={isDisabledPedido}
            isError={!!errors.conta}
            control={controlFinanceiro}
            options={contas}
            changeSelected={(selected) => {
              clearErrorsFinanceiro('conta');
              setValueFinanceiro('conta', selected);
            }}
          />
        </Col>
        <Col sm={12} md={2}>
          <InputMoney
            label="Desconto"
            placeholder="0,00"
            min={0}
            max={subValor}
            maxLength={10}
            name="val_desconto"
            register={register}
            control={control}
            disabled={isDisabledPedido}
            showIcon
            isError={false}
            onInput={(ev: any) => {
              handleCalcDesconto(
                'val_desconto',
                convertToDecimal(formatToCurrency(ev.target.value)),
              );
              setValue('val_desconto', formatToCurrency(ev.target.value));
            }}
          />
        </Col>
        <Col sm={12} md={2}>
          <InputMoney
            label="Acréscimo"
            placeholder="0,00"
            min={0}
            maxLength={10}
            name="val_acrescimo"
            register={register}
            control={control}
            disabled={isDisabledPedido}
            showIcon
            isError={false}
            onInput={(ev: any) => {
              handleCalcDesconto(
                'val_acrescimo',
                convertToDecimal(formatToCurrency(ev.target.value)),
              );
              setValue('val_acrescimo', formatToCurrency(ev.target.value));
            }}
          />
        </Col>
      </Row>
      <div className="d-flex align-items-end justify-content-end gap-5">
        <div>
          <h5
            style={{
              textAlign: 'right',
              display: 'inline-block',
            }}
          >
            {`Valor Devido: R$${
              !subTotalFinanceiro
                ? formatCurrencyAsText(totalFinanceiro || 0)
                : formatCurrencyAsText(subTotalFinanceiro || 0)
            }`}
          </h5>
        </div>
        <div>
          <h5
            style={{
              textAlign: 'right',
              display: 'block',
              marginTop: '20px',
            }}
          >
            {`Total Financeiro: R$${formatCurrencyAsText(
              totalFinanceiro || 0,
            )}`}
          </h5>
        </div>
      </div>
      <Row>
        <Col>
          <Separator labelText="Observação" sidePadding="0 0" />
          <div style={{ marginTop: '-35px' }}>
            <InputText
              maxLength={150}
              caseInput="upper"
              placeholder="Digite aqui a observação"
              name="des_observacao"
              control={control}
              toLowerCase={false}
              register={register}
              isError={!!errors.des_observacao}
            />
          </div>
        </Col>
      </Row>
    </>
  );
};
